import React from 'react';

import { useQuery } from '@apollo/client';
import { Box, Stack, useMediaQuery, type Theme } from '@mui/material';

import DataLoader from '@ivy/components/molecules/DataLoader';
import DisclaimerToast from '@ivy/components/molecules/DisclaimerToast';
import OrgSurveyCta from '@ivy/components/molecules/OrgSurveyCta';
import ExplainerCta from '@ivy/components/organisms/ExplainerCta';
import { type Profession } from '@ivy/constants/clinician';
import { gql } from '@ivy/gql/types';
import OrgWhitelabelCta from '@ivy/views/org/OrgShow/OrgProfile/OrgWhitelabelCta';

import AcepCharacteristics from './AcepCharacteristics';
import AcepInfo from './AcepInfo';
import AcepOperations from './AcepOperations';
import AcepPolicies from './AcepPolicies';
import OrgAbout from './OrgAbout';
import OrgAtAGlance from './OrgAtAGlance';
import OrgFacilities from './OrgFacilities';
import OrgJobs from './OrgJobs';

const OrgProfile_OrgQDoc = gql(/* GraphQL */ `
	query OrgProfile_Org(
		$orgId: uuid!
		$professionsFilter: profession_comparison_exp!
	) {
		org: organization_by_pk(id: $orgId) {
			id
			...OrgWhitelabelCta_Org
			...OrgAtAGlance_Org
			...OrgAbout_Org
			...OrgFacilities_Org
			...OrgJobs_Org
			...AcepInfo_Org
			...AcepCharacteristics_Org
			...AcepOperations_Org
			...AcepPolicies_Org
			surveys: acep_org_surveys(
				where: { accepted_at: { _is_null: false } }
				order_by: [{ accepted_at: desc_nulls_last }]
				limit: 1
			) {
				id
			}
		}
	}
`);

export interface OrgProfileProps {
	orgId: string;
	professions?: Profession[];
	showAcepSurvey?: boolean;
	showAcepSurveyCta?: boolean;
	showAcepSurveyDisclaimer?: boolean;
	showDescription?: boolean;
	showWhitelabelCta?: boolean;
}

const COLUMN_1_WIDTH = {
	xs: '100%',
	md: '60%',
};
const COLUMN_2_WIDTH = '40%';
const COLUMN_SPACING = {
	md: 6,
	lg: 10,
};
const ROW_SPACING = {
	xs: 8,
	lg: 15,
};
const ROW_SPACING_HALF = Object.entries(ROW_SPACING).reduce(
	(tot, [k, v]) => ({
		...tot,
		[k]: v / 2,
	}),
	{},
);

const OrgProfile = ({
	orgId,
	professions,
	showDescription = false,
	showAcepSurvey = false,
	showAcepSurveyCta = false,
	showAcepSurveyDisclaimer = false,
	showWhitelabelCta = false,
}: OrgProfileProps) => {
	const { data, loading, error } = useQuery(OrgProfile_OrgQDoc, {
		variables: {
			orgId,
			professionsFilter: professions
				? {
						_in: professions,
				  }
				: {},
		},
	});
	const isLteMd = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

	return (
		<DataLoader variant='circular' data={data} loading={loading} error={error}>
			{() => (
				<Box
					sx={{
						mt: ROW_SPACING_HALF,
						mb:
							showAcepSurvey && showAcepSurveyCta
								? undefined
								: ROW_SPACING_HALF,
					}}
				>
					<Box
						sx={(theme) => ({
							[theme.breakpoints.up('md')]: {
								display: 'flex',
								alignItems: 'center',
							},
						})}
					>
						<Box
							sx={(theme) => ({
								[theme.breakpoints.up('md')]: {
									width: COLUMN_1_WIDTH,
									// Use padding instead of margin so that overflow won't cause flex to ignore the margin
									pr: COLUMN_SPACING,
								},
							})}
						>
							<OrgAtAGlance org={data!.org!} />
						</Box>
						{!isLteMd && (
							// We use JavaScript breakpoints since Chrome will not display icons used in multiple places
							// if one of them has display: none
							<Box
								sx={{
									width: COLUMN_2_WIDTH,
								}}
							>
								{showWhitelabelCta ? (
									<OrgWhitelabelCta org={data!.org!} />
								) : (
									<ExplainerCta />
								)}
							</Box>
						)}
					</Box>
					<Box
						sx={{
							display: 'flex',
							mt: ROW_SPACING,
						}}
					>
						<Stack
							sx={{
								// Use padding instead of margin so that overflow won't cause flex to ignore the margin
								pr: COLUMN_SPACING,
								width: COLUMN_1_WIDTH,
								maxWidth: {
									xs: '100%',
									md: 'none',
								},
							}}
							spacing={ROW_SPACING}
						>
							{showDescription && <OrgAbout org={data!.org!} />}
							{isLteMd && (
								// We use JavaScript breakpoint here because using CSS will still
								// render a margin between this Stack and the stuff below even if display is none
								<Stack spacing={ROW_SPACING}>
									<OrgFacilities org={data!.org!} />
									<OrgJobs org={data!.org!} />
									{showWhitelabelCta ? (
										<OrgWhitelabelCta org={data!.org!} />
									) : (
										<ExplainerCta />
									)}
								</Stack>
							)}
							{showAcepSurvey && (
								<>
									<AcepInfo org={data!.org!} />
									<AcepCharacteristics org={data!.org!} />
									<AcepOperations org={data!.org!} />
									<AcepPolicies org={data!.org!} />
									{showAcepSurveyDisclaimer && !!data!.org!.surveys.length && (
										<DisclaimerToast sessionKey='acepOrgProfileDisclaimer'>
											The sections on group information, characteristics,
											operations, and ACEP policies are self-reported by
											employers. Responses do not undergo independent
											verification by ACEP.
										</DisclaimerToast>
									)}
								</>
							)}
						</Stack>
						<Stack
							sx={{
								display: {
									xs: 'none',
									md: 'initial',
								},
								width: COLUMN_2_WIDTH,
							}}
							spacing={ROW_SPACING}
						>
							<OrgFacilities org={data!.org!} />
							<OrgJobs org={data!.org!} />
						</Stack>
					</Box>
					{showAcepSurvey && showAcepSurveyCta && (
						<Box
							sx={{
								mt: ROW_SPACING,
							}}
						>
							<OrgSurveyCta />
						</Box>
					)}
				</Box>
			)}
		</DataLoader>
	);
};

export default OrgProfile;
